// src/components/kuestion/kb/KnowledgeBase.jsx
//
// The Kuestion Knowledge Base, at <mount>/kb (design page of 19 Sep 2026).
// Public: readable without signing in, so it has its own header and never
// asks for a session. A session the tab happens to hold is used for one
// thing only — signing the feedback the reader sends (KB contract §4).
//
// Addresses under kb/ (design note "KB entry points and routing", 19 Sep):
//   (none)                 home: search, browse, topics
//   articles               the index and reading pane, first article open
//   articles?topic=<slug>  the index narrowed to one topic
//   articles/<id>          one article, by slug — or by KB number:
//   articles/KB-203        redirects to the slug, so support replies can be pasted
// Earlier spellings (browse, browse/<topic>, <slug>) redirect to these.
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useStudentAuthOptional } from '../../../context/StudentAuthContext';
import { ThemeProvider, ThemeToggle } from '../shared/ThemeContext';
import AppNoticeboard from '../shared/AppNoticeboard';
import { Mark } from '../shared/Bits';
import { BookIcon } from '../shared/Icons';
import { ARTICLES, GROUPS, KBNUM, articleById, articleByNumber, searchArticles, normaliseQuery, groupSlug, groupBySlug } from './kbArticles';
import SHOTS from './kbShots';
import FeedbackBlock from './FeedbackBlock';
import { kq, KUESTION_PUBLIC_ADDRESS } from '../../../config/routes';
import '../campus.css';
import '../kb.css';

const SearchGlyph = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><line x1="15.5" y1="15.5" x2="21" y2="21" /></svg>
);
const CrossGlyph = ({ w = 2.6 }) => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
);
const BackGlyph = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="20" y1="12" x2="5" y2="12" /><polyline points="11 6 5 12 11 18" /></svg>
);
const ArrowGlyph = () => (
  <svg className="kqkb-browseall__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="4" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" /></svg>
);

const isNeutralAudience = (aud) => aud.indexOf('Student') === 0 || aud === 'Everyone';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Derived, not written down: a hardcoded count becomes a factual error in a
// user guide the moment an article is added.
const SEARCH_PLACEHOLDER = `Search ${ARTICLES.length} articles, or type a KB number`;

function SearchBox({ value, onChange, small = false, id }) {
  return (
    <div className={`kqkb-search${small ? ' kqkb-search--sm' : ''}`}>
      <span className="kqkb-search__icon"><SearchGlyph /></span>
      <input id={id} type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={SEARCH_PLACEHOLDER} aria-label="Search the knowledge base" autoComplete="off" />
      {value && (
        <button type="button" className="kqkb-search__clear" onClick={() => onChange('')} title="Clear the search" aria-label="Clear the search"><CrossGlyph w={small ? 2.8 : 2.6} /></button>
      )}
    </div>
  );
}

function Blocks({ blocks }) {
  let step = 0;
  let fig = 0;
  return blocks.map((b, i) => {
    const key = `${b.t}-${i}`;
    switch (b.t) {
      case 'h': return <h2 key={key} className="kqkb-h2">{b.text}</h2>;
      case 'p': return <p key={key} className="kqkb-p">{b.text}</p>;
      case 'step':
        step += 1;
        return (
          <div key={key} className="kqkb-step">
            <span className="kqkb-step__n" aria-hidden="true">{step}</span>
            <span className="kqkb-step__text">
              <span className="kqkb-step__title">{b.text}</span>
              <span className="kqkb-step__body">{b.body}</span>
            </span>
          </div>
        );
      case 'bullet':
        return <div key={key} className="kqkb-bullet"><span className="kqkb-bullet__dot" aria-hidden="true" /><span>{b.text}</span></div>;
      case 'shot': {
        fig += 1;
        const src = SHOTS[b.src];
        if (!src) return null; // a capture not shipped yet: no broken frame
        return (
          <figure key={key} className="kqkb-fig">
            <img className="kqkb-fig__img" src={src} alt={b.text} loading="lazy" width="909" height="540" />
            <figcaption><b>Fig. {fig}</b><span>{b.text}</span></figcaption>
          </figure>
        );
      }
      case 'note':
        return (
          <div key={key} className="kqkb-note">
            <span className="kqkb-note__bar" aria-hidden="true" />
            <span className="kqkb-note__text"><span className="kqkb-note__head">{b.head}</span><span className="kqkb-note__body">{b.text}</span></span>
          </div>
        );
      case 'warn':
        return <div key={key} className="kqkb-warn"><div className="kqkb-warn__head">{b.head}</div><div className="kqkb-warn__body">{b.text}</div></div>;
      case 'faq':
        return <div key={key} className="kqkb-faq"><div className="kqkb-faq__q">{b.head}</div><div className="kqkb-faq__a">{b.text}</div></div>;
      default: return null;
    }
  });
}

function KnowledgeBaseInner() {
  const navigate = useNavigate();
  const { '*': rest = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');

  // Which session, if any, signs the feedback. Never required.
  const { user } = useAuth();
  const studentAuth = useStudentAuthOptional();
  const session = user?.token ? { kind: 'user', token: user.token }
    : studentAuth?.token ? { kind: 'student', token: studentAuth.token }
      : { kind: 'anon', token: null };

  // ── where are we ────────────────────────────────────────────────────
  const parts = rest.split('/').filter(Boolean);
  let view = 'home';
  let topic = null;
  let article = null;
  let redirect = null;
  const topicParam = searchParams.get('topic');
  const topicQuerySuffix = topicParam ? `?topic=${topicParam}` : '';
  if (parts[0] === 'articles') {
    view = 'articles';
    topic = topicParam ? groupBySlug(topicParam) : null;
    if (parts[1]) {
      const bySlug = articleById(parts[1]);
      const byNum = bySlug || articleByNumber(parts[1]);
      if (byNum) {
        article = byNum;
        if (!bySlug) redirect = kq(`kb/articles/${byNum.id}`) + topicQuerySuffix; // KB-203 → its slug
      } else {
        redirect = kq('kb/articles') + topicQuerySuffix;
      }
    } else {
      // the index with the first article of the scope open
      article = ARTICLES.find((a) => !topic || a.group === topic) || ARTICLES[0];
    }
  } else if (parts[0] === 'browse') {
    redirect = kq('kb/articles') + (parts[1] ? `?topic=${parts[1]}` : '');
  } else if (parts[0]) {
    const a = articleById(parts[0]) || articleByNumber(parts[0]);
    redirect = a ? kq(`kb/articles/${a.id}`) + topicQuerySuffix : kq('kb');
  }
  const articleId = article ? article.id : null;

  useEffect(() => {
    const before = document.title;
    document.title = article && view === 'articles' ? `${article.title} · Kuestion knowledge base` : 'Knowledge base · Kuestion';
    return () => { document.title = before; };
  }, [article, view]);

  // A new article starts at the top, the way a new page would.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [articleId, view]);

  // ── navigation helpers ──────────────────────────────────────────────
  const topicQuery = topic ? `?topic=${groupSlug(topic)}` : '';
  const goHome = () => { setQuery(''); navigate(kq('kb')); };
  const browseAll = () => { setQuery(''); navigate(kq('kb/articles')); };
  const openTopic = (g) => { setQuery(''); navigate(kq('kb/articles') + `?topic=${groupSlug(g)}`); };
  const open = (id) => navigate(kq(`kb/articles/${id}`) + topicQuery);
  const clearTopic = () => navigate(kq(`kb/articles/${article.id}`));
  // The header crumb follows the route rather than staying static.
  const crumbPath = view === 'articles' ? `${KUESTION_PUBLIC_ADDRESS}/kb/articles` : `${KUESTION_PUBLIC_ADDRESS}/kb`;

  // ── search ──────────────────────────────────────────────────────────
  const q = normaliseQuery(query);
  const hits = useMemo(() => searchArticles(query), [query]);
  const scoped = topic ? hits.filter((a) => a.group === topic) : hits;
  const resultLine = q
    ? `${plural(scoped.length, 'article matches', 'articles match')} “${query.trim()}”${topic ? ` in ${topic}` : ''}`
    : topic
      ? `${plural(scoped.length, 'article', 'articles')} · ${topic}`
      : `${ARTICLES.length} articles · Kuestion user guide`;

  if (redirect) return <Navigate to={redirect} replace />;

  const header = (
    <header className="kqkb-header">
      <div className="kqkb-max kqkb-header__in">
        <a className="kqkb-header__mark" href={kq()} title="Kuestion"><Mark /></a>
        <a className="kqkb-header__brand" href={kq()}>Kuestion</a>
        <span className="kqkb-header__addr">{crumbPath}</span>
        <span style={{ flex: 1 }} />
        {view === 'articles' && (
          <button type="button" className="kqkb-header__home" onClick={goHome}><BackGlyph />KB home</button>
        )}
        <AppNoticeboard store="rw" />
        <ThemeToggle />
        <span className="kqkb-header__kicker">Knowledge base</span>
      </div>
    </header>
  );

  if (view === 'home') {
    return (
      <div className="kqc kqkb">
        {header}
        <main>
          <section className="kqkb-hero">
            <div className="kqkb-max">
              <div className="kqkb-kicker">Kuestion user guide</div>
              <h1>Knowledge base</h1>
              <p className="kqkb-hero__lede">Every screen in Kuestion, written up. Search for a word, or type the KB number printed on the article someone sent you.</p>
              <SearchBox id="kb-search" value={query} onChange={setQuery} />
              <div className="kqkb-hint">Try a word like <strong>password</strong>, or a number like <strong>KB-101</strong>.</div>

              {q && (
                <div className="kqkb-hits" aria-live="polite">
                  <div className="kqkb-hits__line">{resultLine}</div>
                  {hits.map((a) => (
                    <button key={a.id} type="button" className="kqkb-hit" onClick={() => open(a.id)}>
                      <span className="kqkb-hit__num">{KBNUM[a.id]}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className="kqkb-hit__title">{a.title}</span>
                        <span className="kqkb-hit__meta">{a.group} · {a.audience} · {a.read}</span>
                      </span>
                    </button>
                  ))}
                  {hits.length === 0 && (
                    <div className="kqkb-nohits">Nothing matches that. Try a plainer word &mdash; the search reads whole articles, so <strong>extra time</strong> finds the article even when the title does not say it.</div>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="kqkb-browse">
            <div className="kqkb-max">
              <button type="button" className="kqkb-browseall" onClick={browseAll}>
                <span className="kqkb-browseall__icon"><BookIcon /></span>
                <span className="kqkb-browseall__text">
                  <span className="kqkb-browseall__title">Browse the guides</span>
                  <span className="kqkb-browseall__meta">{ARTICLES.length} articles across {GROUPS.length} topics, in the order you meet them</span>
                </span>
                <ArrowGlyph />
              </button>

              <span className="kqkb-kicker">Or go straight to a topic</span>
              <div className="kqkb-topics">
                {GROUPS.map((g, gi) => {
                  const items = ARTICLES.filter((a) => a.group === g);
                  return (
                    <button key={g} type="button" className="kqkb-topic" onClick={() => openTopic(g)}>
                      <span className="kqkb-topic__range">KB-{gi + 1}00</span>
                      <span className="kqkb-topic__name">{g}</span>
                      <span className="kqkb-topic__count">{plural(items.length, 'article', 'articles')}</span>
                      <span className="kqkb-topic__sample">{items.slice(0, 3).map((a) => a.title).join(' · ')}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  // ── articles: index + reading pane ──────────────────────────────────
  const groups = GROUPS.filter((g) => !topic || g === topic)
    .map((g) => ({ name: g, items: scoped.filter((a) => a.group === g) }))
    .filter((g) => g.items.length > 0);
  const related = (article.related || []).map((id) => articleById(id)).filter(Boolean);

  return (
    <div className="kqc kqkb">
      {header}
      <div className="kqkb-max kqkb-body">
        <nav className="kqkb-nav" aria-label="Articles">
          <SearchBox id="kb-search-index" value={query} onChange={setQuery} small />
          <div className="kqkb-nav__line">{resultLine}</div>

          {topic && (
            <div className="kqkb-nav__chips">
              <span className="kqkb-chip">
                {topic}
                <button type="button" className="kqkb-chip__x" onClick={clearTopic} title="Show every topic" aria-label="Show every topic"><CrossGlyph w={3.2} /></button>
              </span>
              <button type="button" className="kqkb-linkbtn" onClick={clearTopic}>Show every topic</button>
            </div>
          )}

          {groups.map((g) => (
            <div key={g.name} className="kqkb-nav__group">
              <div className="kqkb-nav__grouphead">{g.name}</div>
              {g.items.map((a) => (
                <button key={a.id} type="button" className={`kqkb-nav__item${a.id === article.id ? ' is-on' : ''}`} aria-current={a.id === article.id ? 'page' : undefined} onClick={() => open(a.id)}>
                  <strong>{a.title}</strong>
                  <span>{KBNUM[a.id]} · {a.audience}</span>
                </button>
              ))}
            </div>
          ))}
          {groups.length === 0 && <div className="kqkb-nav__empty">Nothing matches that here. Clear the search, or try a plainer word.</div>}
        </nav>

        <main className="kqkb-read">
          <div className="kqkb-crumb">
            <button type="button" className="kqkb-linkbtn" onClick={goHome}>Knowledge base</button>
            <span className="kqkb-crumb__sep">/</span>
            <button type="button" className="kqkb-linkbtn" onClick={() => openTopic(article.group)}>{article.group}</button>
            <span className="kqkb-crumb__sep">/</span>
            <span className="kqkb-crumb__here">{article.title}</span>
          </div>

          <div className="kqkb-tags">
            <span className="kqkb-num">{KBNUM[article.id]}</span>
            <span className={`kqkb-aud${isNeutralAudience(article.audience) ? ' kqkb-aud--neutral' : ''}`}>{article.audience}</span>
            <span className="tag tag-neutral">{article.where}</span>
            <span className="tag tag-neutral">{article.read} read</span>
          </div>

          <h1>{article.title}</h1>
          <p className="kqkb-read__lede">{article.lede}</p>
          <div className="kqkb-hr" />

          <Blocks blocks={article.blocks} />

          {related.length > 0 && (
            <>
              <div className="kqkb-hr kqkb-hr--soft" />
              <div className="kqkb-kicker" style={{ display: 'block', marginBottom: 12 }}>Related</div>
              <div className="kqkb-related">
                {related.map((r) => (
                  <button key={r.id} type="button" className="kqkb-rel" onClick={() => open(r.id)}>
                    <strong>{r.title}</strong>
                    <span>{r.group} · {r.read}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          <FeedbackBlock key={article.id} article={article} session={session} />
        </main>
      </div>
    </div>
  );
}

export default function KnowledgeBase() {
  return (
    <ThemeProvider>
      <KnowledgeBaseInner />
    </ThemeProvider>
  );
}
